// Price animation utility using requestAnimationFrame
// Replaces per-element animation from public/selected-cards.js

const PRICE_ANIMATION_DURATION = 500; // 0.5 seconds

/**
 * Animate a numeric value from `from` to `to`, calling `onFrame` each frame.
 * Uses ease-out cubic for smooth deceleration.
 *
 * @param {number} from - Starting value
 * @param {number} to - Target value
 * @param {(value: number) => void} onFrame - Called each frame with interpolated value
 * @param {number} [duration=1000] - Animation duration in ms
 * @returns {{ cancel: () => void }} Object with cancel method for cleanup
 */
export function animateValue(from, to, onFrame, duration = PRICE_ANIMATION_DURATION) {
  const delta = Math.abs(to - from);

  // Skip animation for negligible changes
  if (delta < 0.01) {
    onFrame(to);
    return { cancel: () => {} };
  }

  let animationId = null;
  const startTime = performance.now();

  function update(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Ease-out cubic: 1 - (1-t)^3
    const eased = 1 - Math.pow(1 - progress, 3);

    const currentValue = from + (to - from) * eased;
    onFrame(currentValue);

    if (progress < 1) {
      animationId = requestAnimationFrame(update);
    } else {
      // Ensure we end exactly on target
      onFrame(to);
      animationId = null;
    }
  }

  animationId = requestAnimationFrame(update);

  return {
    cancel: () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
  };
}
